import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { PRESET_ROLES as ROLE_KEYS, roleLabel } from "../../utils/jsaLabels";

// Stored in English; shown translated via roleLabel.
const PRESET_ROLES = Object.keys(ROLE_KEYS);

export default function SignatureBlock({ signatures, onChange, readOnly }) {
  const { t, i18n } = useTranslation();
  const [customRole, setCustomRole] = useState("");
  const [customName, setCustomName] = useState("");

  const cyan = "#00f5ff";
  const border = "rgba(0,245,255,0.15)";

  function handleChange(index, name) {
    if (readOnly) return;
    const updated = [...signatures];
    updated[index] = {
      ...updated[index],
      name,
      signedAt: name ? new Date().toISOString() : null,
    };
    onChange(updated);
  }

  function addCustomSignature() {
    if (!customRole.trim()) return;
    const updated = [
      ...signatures,
      {
        role: customRole.trim(),
        name: customName.trim(),
        signedAt: customName.trim() ? new Date().toISOString() : null,
      },
    ];
    onChange(updated);
    setCustomRole("");
    setCustomName("");
  }

  function removeSignature(index) {
    if (readOnly) return;
    const updated = signatures.filter((_, i) => i !== index);
    onChange(updated);
  }

  // Ensure preset roles always exist
  const allSignatures = [...signatures];
  PRESET_ROLES.forEach((role) => {
    if (!allSignatures.find((s) => s.role === role)) {
      allSignatures.push({ role, name: "", signedAt: null });
    }
  });

  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        {allSignatures.map((sig, idx) => {
          const isPreset = PRESET_ROLES.includes(sig.role);
          const originalIdx = signatures.findIndex((s) => s.role === sig.role);
          return (
            <div
              key={`${sig.role}-${idx}`}
              style={{
                backgroundColor: "rgba(0,245,255,0.03)",
                border: `1px solid ${border}`,
                borderRadius: "6px",
                padding: "10px 14px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "6px",
                }}
              >
                <div
                  style={{
                    color: cyan,
                    fontSize: "9px",
                    letterSpacing: "2px",
                    fontWeight: "700",
                  }}
                >
                  {roleLabel(t, sig.role).toUpperCase()}
                </div>
                {!isPreset && !readOnly && (
                  <button
                    aria-label={t("jsa.removeSignature")}
                    onClick={() => removeSignature(originalIdx)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "rgba(255,68,102,0.6)",
                      cursor: "pointer",
                      fontSize: "12px",
                      padding: "0",
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
              <input
                type="text"
                value={sig.name || ""}
                onChange={(e) => {
                  if (originalIdx === -1) {
                    onChange([
                      ...signatures,
                      {
                        role: sig.role,
                        name: e.target.value,
                        signedAt: e.target.value
                          ? new Date().toISOString()
                          : null,
                      },
                    ]);
                  } else {
                    handleChange(originalIdx, e.target.value);
                  }
                }}
                placeholder={
                  readOnly ? "________________" : t("jsa.signPlaceholder")
                }
                disabled={readOnly}
                style={{
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  borderBottom: `1px solid rgba(0,245,255,0.3)`,
                  color: "white",
                  fontSize: "14px",
                  fontFamily: "'Dancing Script', cursive, 'Segoe UI'",
                  padding: "4px 0",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {sig.signedAt && (
                <div
                  style={{
                    color: "rgba(0,245,255,0.4)",
                    fontSize: "9px",
                    marginTop: "4px",
                    letterSpacing: "1px",
                  }}
                >
                  {t("jsa.signedAt", {
                    when: new Date(sig.signedAt).toLocaleString(i18n.language === "es" ? "es-US" : "en-US"),
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ADD CUSTOM SIGNATURE */}
      {!readOnly && (
        <div
          style={{
            backgroundColor: "rgba(0,245,255,0.03)",
            border: `1px dashed rgba(0,245,255,0.2)`,
            borderRadius: "8px",
            padding: "12px",
          }}
        >
          <div
            style={{
              color: cyan,
              fontSize: "9px",
              letterSpacing: "2px",
              fontWeight: "700",
              marginBottom: "10px",
            }}
          >
            {t("jsa.addSignature")}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr auto",
              gap: "8px",
              alignItems: "end",
            }}
          >
            <div>
              <div
                style={{
                  color: "rgba(0,245,255,0.4)",
                  fontSize: "9px",
                  letterSpacing: "1px",
                  marginBottom: "4px",
                }}
              >
                {t("jsa.roleTitle")}
              </div>
              <input
                type="text"
                value={customRole}
                onChange={(e) => setCustomRole(e.target.value)}
                placeholder={t("jsa.rolePlaceholder")}
                style={{
                  width: "100%",
                  backgroundColor: "rgba(0,245,255,0.05)",
                  border: "1px solid rgba(0,245,255,0.2)",
                  borderRadius: "4px",
                  padding: "6px 10px",
                  color: "white",
                  fontSize: "12px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div>
              <div
                style={{
                  color: "rgba(0,245,255,0.4)",
                  fontSize: "9px",
                  letterSpacing: "1px",
                  marginBottom: "4px",
                }}
              >
                {t("jsa.nameOptional")}
              </div>
              <input
                type="text"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder={t("jsa.signLater")}
                style={{
                  width: "100%",
                  backgroundColor: "rgba(0,245,255,0.05)",
                  border: "1px solid rgba(0,245,255,0.2)",
                  borderRadius: "4px",
                  padding: "6px 10px",
                  color: "white",
                  fontSize: "12px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <button
              onClick={addCustomSignature}
              disabled={!customRole.trim()}
              style={{
                background: customRole.trim()
                  ? "linear-gradient(135deg, #00f5ff, #0080ff)"
                  : "rgba(0,245,255,0.1)",
                color: customRole.trim() ? "#020817" : "rgba(0,245,255,0.3)",
                fontWeight: "700",
                fontSize: "10px",
                padding: "6px 14px",
                borderRadius: "4px",
                border: "none",
                cursor: customRole.trim() ? "pointer" : "not-allowed",
                letterSpacing: "1px",
                whiteSpace: "nowrap",
              }}
            >
              {t("jsa.add")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
